// Concrete `MahjongMenuContext` builder. Wires the per-app dependencies
// (settings, the game scene host) into the small interface the menu screens
// consume, so `main.ts` only constructs the dependency graph and the screens
// stay unaware of it. Kept in `scenes/` alongside the other composition glue.

import { startNewGame } from './Bootstrap.js';
import { LessonController } from '../lessons/LessonController.js';
import { SimplifiedRuleset } from '../game/ruleset/SimplifiedRuleset.js';
import { ClassicalRuleset } from '../game/ruleset/ClassicalRuleset.js';
import { createMatch } from '../match/MatchState.js';
import type { GameScene } from './GameScene.js';
import type { LessonScene } from './LessonScene.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { MahjongMenuContext, OnlineMenuApi, Scene } from '../ui/types.js';
import type { Settings } from '../settings/types.js';

export interface MenuContextDeps {
  readonly gameScene: GameScene;
  readonly lessonScene: LessonScene;
  readonly settingsManager: SettingsManager;
  readonly navigate: (scene: Scene) => void;
  readonly onSettingsChanged: (before: Settings, after: Settings) => void;
  /** Return from a live lesson to the lesson picker. */
  readonly exitToLessons: () => void;
  /** Online rooms — `scenes/OnlineMenuHost.ts` owns the room and the handoff. */
  readonly online: OnlineMenuApi;
}

export function buildMenuContext(deps: MenuContextDeps): MahjongMenuContext {
  return {
    navigate: deps.navigate,
    online: deps.online,
    getSettings: () => deps.settingsManager.get(),
    updateSettings: (partial) => {
      const before = deps.settingsManager.get();
      deps.settingsManager.update(partial);
      deps.onSettingsChanged(before, deps.settingsManager.get());
    },
    startFreePlay: () => {
      // Navigate first so the canvas is visible before the scene starts its
      // rAF loop; then hand it a freshly dealt hand.
      deps.navigate('game');
      const settings = deps.settingsManager.get();
      if (settings.variant === 'classical') {
        const matchState = createMatch(settings.classicalStartingPoints);
        deps.gameScene.mount({
          state: startNewGame(undefined, ClassicalRuleset, matchState.dealer),
          ruleset: ClassicalRuleset,
          matchState,
        });
        return;
      }
      deps.gameScene.mount({ state: startNewGame(), ruleset: SimplifiedRuleset });
    },
    startLesson: (script) => {
      // Same canvas handoff as free play, but the LessonScene owns the canvas
      // and drives the gated LessonController instead of GameScene + BotDriver.
      deps.navigate('game');
      deps.lessonScene.mount(new LessonController(script), deps.exitToLessons);
    },
  };
}
